import { KEYCODE } from '@/constants';

/**
 * Class definition for `wp-accordion` element
 *
 * @class WPAccordion
 * @extends {HTMLElement}
 */
class WPAccordion extends HTMLElement {
  constructor(self) {
    self = super(self);
    this._selectedTab = null;
    this._hash = window.location.hash
      ? this.querySelector(window.location.hash)
      : null;
    this._id = this.id
      ? this.id
      : 'tabs-' + Math.random().toString(36).substr(2, 5);
    return self;
  }

  /**
   * Tag name of element
   *
   * @readonly
   * @static
   */
  static get is() {
    return 'wp-accordion';
  }

  /**
   * Getter for `multiselect` attribute
   */
  get multiselect() {
    return this.hasAttribute('multiselect');
  }

  /**
   * Setter for `multiselect` attribute
   */
  set multiselect(value) {
    if (Boolean(value)) {
      this.setAttribute('multiselect', '');
    } else {
      this.removeAttribute('multiselect');
    }
  }

  /**
   * Getter for `prefix` attribute
   */
  get prefix() {
    return this.hasAttribute('prefix')
      ? this.getAttribute('prefix')
      : 'c-accordion';
  }

  /**
   * Setter for `prefix` attribute
   */
  set prefix(value) {
    if (String(value.length)) {
      this.setAttribute('prefix', value);
    } else {
      this.removeAttribute('prefix');
    }
  }

  /**
   * Getter for `heading` attribute
   */
  get heading() {
    return this.hasAttribute('heading');
  }

  /**
   * Setter for `heading` attribute
   */
  set heading(value) {
    if (Boolean(value)) {
      this.setAttribute('heading', '');
    } else {
      this.removeAttribute('heading');
    }
  }

  /**
   * Returns array of tabs
   *
   * @returns {HTMLElement[]} Array of tabs
   * @private
   * @readonly
   */
  get _tabs() {
    return [...this.querySelectorAll('wp-accordion-heading')];
  }

  /**
   * Returns array of tabpanels
   *
   * @returns {HTMLElement[]} Array of tabpanels
   * @private
   * @readonly
   */
  get _tabpanels() {
    return [...this.querySelectorAll('wp-accordion-content')];
  }

  /**
   * Returns the current index of selected tab within tabs array
   *
   * @returns {Number} Current index of selected tab
   * @readonly
   */
  get _index() {
    let currentIndex = -1;

    this._tabs.forEach((tab, index) => {
      if (tab === this._selectedTab) {
        currentIndex = index;
      }
    });

    return currentIndex;
  }

  /**
   * Lifecycle event when element is attached to DOM
   */
  connectedCallback() {
    if (this._tabs.length !== this._tabpanels.length) {
      console.warn('Missing tabs or tabpanels');
      return;
    }

    // When child elements are defined, setup tabs
    Promise.all([
      customElements.whenDefined('wp-accordion-heading'),
      customElements.whenDefined('wp-accordion-content'),
    ]).then(() => {
      this._setupTabs();
      this._setupTabpanels();
      this.classList.add(this.prefix);
      this.setAttribute('role', 'tablist');

      if (this.multiselect) {
        this.setAttribute('aria-multiselectable', true);
      }

      setTimeout(() => {
        // Open any tabs that are expanded
        this._tabs
          .filter((tab) => tab.expanded)
          .forEach((tab) => {
            this._expandTab(tab);
          });

        // Select first tab so keyboard events work
        this._tabs[0].selected = true;

        // Add event listeners
        this.addEventListener('tabselected', this._handleTabSelected, false);
        this.addEventListener('tabexpanded', this._handleTabExpanded, false);
        this.addEventListener('tabcollapsed', this._handleTabCollapsed, false);
        this.addEventListener('keydown', this._handleKeyDown, false);

        // If has in window
        if (
          this._hash &&
          this._hash.tagName.toLowerCase() === 'wp-accordion-heading'
        ) {
          this._hash.expanded = true;
        }
      }, 100);

      if (
        this._hash &&
        this._hash.tagName.toLowerCase() === 'wp-accordion-heading'
      ) {
        this._hash.expanded = true;
      }
    });
  }

  /**
   * Setup tabs for interaction and accessibility
   *
   * @private
   */
  _setupTabs() {
    this._tabs.forEach((tab, index) => {
      tab.id = tab.id ? tab.id : `${this._id}-tab-${index}`;
      tab.setAttribute('aria-controls', `${this._id}-tabpanel-${index}`);
      tab.classList.add(`${this.prefix}__tab`);
    });
  }

  /**
   * Setup tabpanels for interaction and accessibility
   *
   * @private
   */
  _setupTabpanels() {
    this._tabpanels.forEach((tabpanel, index) => {
      tabpanel.id = `${this._id}-tabpanel-${index}`;
      tabpanel.setAttribute(
        'aria-labelledby',
        this._tabs[index].id
          ? this._tabs[index].id
          : `${this._id}-tab-${index}`,
      );
      tabpanel.classList.add(`${this.prefix}__tabpanel`);

      // Create container
      const div = document.createElement('div');
      div.className = `${this.prefix}__content`;

      // Create heading
      if (this.heading) {
        const heading = document.createElement('div');
        heading.textContent = this._tabs[index].textContent;
        heading.setAttribute('role', 'heading');
        heading.tabIndex = 0;
        div.appendChild(heading);
      }

      div.innerHTML += tabpanel.innerHTML;
      tabpanel.innerHTML = '';
      tabpanel.appendChild(div);
    });
  }

  /**
   * Event handler for `tabselected` event
   *
   * @private
   * @param {UIEvent} evt
   */
  _handleTabSelected(evt) {
    if (this._selectedTab && this._selectedTab !== evt.detail) {
      this._selectedTab.selected = false;
    }

    this._selectedTab = evt.detail;
  }

  /**
   * Event handler for `tabexpanded` event
   *
   * @private
   * @param {UIEvent} evt
   */
  _handleTabExpanded(evt) {
    this._expandTab(evt.detail);

    // If not multiselectable, close all other tabs
    if (!this.multiselect) {
      this._tabs
        .filter((tab) => tab.expanded)
        .forEach((tab) => {
          if (tab !== evt.detail) {
            tab.expanded = false;
          }
        });
    }
  }

  /**
   * Event handler for `tabcollapsed` event
   *
   * @private
   * @param {UIEvent} evt
   */
  _handleTabCollapsed(evt) {
    this._collapseTab(evt.detail);
  }

  /**
   * Expand a tab
   *
   * @private
   * @param {HTMLElement} tab Tab to expanded
   */
  _expandTab(tab) {
    const tabpanel = document.getElementById(tab.getAttribute('aria-controls'));

    tabpanel.collapsed = false;
  }

  /**
   * Collapse a tab
   *
   * @private
   * @param {HTMLElement} tab Tab to expanded
   */
  _collapseTab(tab) {
    const tabpanel = document.getElementById(tab.getAttribute('aria-controls'));

    tabpanel.collapsed = true;
  }

  /**
   * Event handler for `keydown` event
   *
   * @private
   * @param {UIEvent} evt
   */
  _handleKeyDown(evt) {
    // If focus is not a tab, return
    if (!this._tabs.includes(evt.target)) {
      return;
    }

    this._selectedTab = evt.target;
    const keyCode = evt.which;
    const nextTab = this._tabs[this._index + 1];
    const firstTab = this._tabs[0];
    const previousTab = this._tabs[this._index - 1];
    const lastTab = this._tabs[this._tabs.length - 1];

    switch (keyCode) {
      // Left/Up
      case KEYCODE.LEFT:
      case KEYCODE.UP:
        evt.preventDefault();
        evt.stopPropagation();

        if (!previousTab) {
          lastTab.focus();
        } else {
          previousTab.focus();
        }

        break;

      // Right/Down
      case KEYCODE.RIGHT:
      case KEYCODE.DOWN:
        evt.preventDefault();
        evt.stopPropagation();

        if (!nextTab) {
          firstTab.focus();
        } else {
          nextTab.focus();
        }

        break;

      // Home
      case KEYCODE.HOME:
        evt.preventDefault();
        evt.stopPropagation();

        firstTab.focus();

        break;

      // End
      case KEYCODE.END:
        evt.preventDefault();
        evt.stopPropagation();

        lastTab.focus();

        break;

      // Enter/Space
      case KEYCODE.SPACE:
      case KEYCODE.ENTER:
        evt.preventDefault();
        evt.stopPropagation();

        this._selectedTab.click();
        break;
      default:
        break;
    }
  }
}

customElements.define(WPAccordion.is, WPAccordion);
